// packages/frontend/src/services/firebaseFunctions.ts
/**
 * Firebase Functions v2 onCall client service
 * Provides a unified interface for calling Firebase Functions with proper error handling and retry logic
 */

import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator,
} from "firebase/functions";
import { app, auth } from "./firebase";
import { createLogger } from "../utils/logger";

// Initialize logger
const logger = createLogger("FirebaseFunctions");

// Initialize Firebase Functions with auth
const functions = getFunctions(app, "us-central1");

// Connect to emulator in development
if (
  import.meta.env.DEV &&
  import.meta.env.VITE_USE_FIREBASE_EMULATORS !== "false"
) {
  try {
    connectFunctionsEmulator(functions, "localhost", 5001);
    logger.info("Connected to Firebase Functions Emulator");
  } catch (error) {
    logger.warn("Firebase Functions emulator connection failed:", error);
  }
}

// Type definitions for function requests and responses
export interface CreateUserRequest {
  email?: string;
  phoneNumber?: string;
  role: "admin" | "therapist" | "client";
  profile: {
    firstName: string;
    lastName: string;
    displayName: string;
    prefix?: string;
    phone?: string;
    avatar?: string;
    title?: string;
  };
  organizationId: string;
}

export interface CreateUserResponse {
  success: boolean;
  data: {
    userId: string;
    email?: string;
    phoneNumber?: string;
    role: string;
    profile: any;
    organizationId: string;
    isOrgOwner: boolean;
    permissions: string[];
    isActive: boolean;
    createdAt: string;
  };
}

export interface GetUserRequest {
  userId: string;
}

export interface GetUserResponse {
  success: boolean;
  data: {
    userId: string;
    email?: string;
    phoneNumber?: string;
    role: string;
    profile: any;
    organizationId: string;
    isOrgOwner: boolean;
    permissions: string[];
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  };
}

export interface UpdateUserRequest {
  userId: string;
  data: {
    email?: string;
    phoneNumber?: string;
    profile?: Partial<CreateUserRequest["profile"]>;
    isActive?: boolean;
  };
}

export interface UpdateUserResponse {
  success: boolean;
  data: {
    user: {
      id: string;
      email: string;
      phoneNumber: string;
      role: string;
      profile: {
        firstName: string;
        lastName: string;
        displayName: string;
        prefix?: string;
        phone?: string;
        avatar?: string;
        title?: string;
      };
      organizationId: string;
      isActive: boolean;
      isVerified: boolean;
      createdAt: string;
      updatedAt: string;
    };
  };
  metadata?: {
    timestamp: string;
    requestId: string;
    executionTime?: number;
  };
  error?: {
    message?: string;
  };
}

export interface DeleteUserRequest {
  userId: string;
}

export interface DeleteUserResponse {
  success: boolean;
  data: {
    userId: string;
    deletedAt: string;
  };
}

export interface ListUsersRequest {
  pageSize?: number;
  pageToken?: string;
  search?: string;
  role?: "admin" | "therapist" | "client";
  organizationId?: string;
}

export interface ListUsersResponse {
  success: boolean;
  data: {
    users: GetUserResponse["data"][];
    nextPageToken?: string;
    totalCount: number;
  };
}

// Client management types
export interface CreateClientRequest {
  profile: {
    firstName: string;
    lastName: string;
    dateOfBirth: string; // Made required
    gender: string; // Required field
    ethnicity: string; // Required field
    phone?: string;
    email?: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      zipCode?: string;
      country?: string;
    };
    emergencyContact?: {
      name?: string;
      relationship?: string;
      phone?: string;
      email?: string;
    };
    bookCoverChildName?: string;
  };
  therapistId: string;
  notes?: string;
}

export interface CreateClientResponse {
  success: boolean;
  data: {
    clientId: string;
    profile: CreateClientRequest["profile"];
    therapistId: string;
    status: "active" | "inactive" | "archived";
    notes?: string;
    createdAt: string;
  };
}

// Client data type used in list responses
export interface ClientData {
  clientId: string;
  profile: CreateClientRequest["profile"];
  therapistId: string;
  status: "active" | "inactive" | "archived";
  notes?: string;
  /**
   * Avatar-only slice of the client's appearance map, as projected by the
   * listClients function (see ClientAvatarAppearance there). Field names match
   * ChildAppearance so this reaches `Client.appearance` via toClient and is read
   * unchanged by resolveClientAvatarImage / hasApprovedProfileImage. The full
   * appearance (physical attributes, previousVersions, notes) is deliberately
   * not part of the list payload.
   */
  appearance?: {
    profileImage?: {
      currentImageId?: string;
      version?: number;
    };
    portraitImagePath?: string;
    portraitUpdatedAt?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface UpdateClientRequest {
  clientId: string;
  data: {
    profile?: Partial<CreateClientRequest["profile"]>;
    therapistId?: string;
    status?: "active" | "inactive" | "archived";
    notes?: string;
  };
}

export interface UpdateClientResponse {
  success: boolean;
  data: {
    clientId: string;
    updatedAt: string;
  };
}

export interface ListClientsRequest {
  pageSize?: number;
  pageToken?: string;
  search?: string;
  therapistId?: string;
  status?: "active" | "inactive" | "archived";
}

export interface ListClientsResponse {
  success: boolean;
  data: {
    clients: ClientData[];
    nextPageToken?: string;
    totalCount: number;
  };
}

// Session management types
export interface CreateSessionRequest {
  clientId: string;
  therapistId: string;
  date: string;
  time: string;
  duration: number;
  notes?: string;
  metadata?: Record<string, any>;
}

export interface CreateSessionResponse {
  success: boolean;
  data: {
    sessionId: string;
    clientId: string;
    therapistId: string;
    date: string;
    time: string;
    duration: number;
    status: "scheduled" | "confirmed" | "completed" | "cancelled";
    notes?: string;
    metadata?: Record<string, any>;
    createdAt: string;
  };
}

export interface GetSessionRequest {
  sessionId: string;
}

export interface GetSessionResponse {
  success: boolean;
  data: {
    sessionId: string;
    clientId: string;
    therapistId: string;
    date: string;
    time: string;
    duration: number;
    status: "scheduled" | "confirmed" | "completed" | "cancelled";
    notes?: string;
    metadata?: Record<string, any>;
    createdAt: string;
    updatedAt: string;
  };
}

export interface UpdateSessionRequest {
  sessionId: string;
  data: {
    date?: string;
    time?: string;
    duration?: number;
    status?: "scheduled" | "confirmed" | "completed" | "cancelled";
    notes?: string;
    metadata?: Record<string, any>;
  };
}

export interface UpdateSessionResponse {
  success: boolean;
  data: {
    sessionId: string;
    updatedAt: string;
  };
}

export interface DeleteSessionRequest {
  sessionId: string;
}

export interface DeleteSessionResponse {
  success: boolean;
  data: {
    sessionId: string;
    deletedAt: string;
  };
}

export interface ListSessionsRequest {
  pageSize?: number;
  pageToken?: string;
  search?: string;
  clientId?: string;
  therapistId?: string;
  status?: "scheduled" | "confirmed" | "completed" | "cancelled";
  dateFrom?: string;
  dateTo?: string;
}

export interface ListSessionsResponse {
  success: boolean;
  data: {
    sessions: GetSessionResponse["data"][];
    nextPageToken?: string;
    totalCount: number;
  };
}

// Custom error classes
export class FirebaseFunctionsError extends Error {
  constructor(
    message: string,
    public code: string,
    public details?: any,
  ) {
    super(message);
    this.name = "FirebaseFunctionsError";
  }
}

export class AuthenticationError extends FirebaseFunctionsError {
  constructor(message: string = "Authentication required") {
    super(message, "unauthenticated");
    this.name = "AuthenticationError";
  }
}

export class AuthorizationError extends FirebaseFunctionsError {
  constructor(message: string = "Insufficient permissions") {
    super(message, "permission-denied");
    this.name = "AuthorizationError";
  }
}

export class ValidationError extends FirebaseFunctionsError {
  constructor(message: string = "Invalid input") {
    super(message, "invalid-argument");
    this.name = "ValidationError";
  }
}

export class NotFoundError extends FirebaseFunctionsError {
  constructor(message: string = "Resource not found") {
    super(message, "not-found");
    this.name = "NotFoundError";
  }
}

export class RateLimitError extends FirebaseFunctionsError {
  constructor(message: string = "Rate limit exceeded") {
    super(message, "resource-exhausted");
    this.name = "RateLimitError";
  }
}

// Retry configuration
interface RetryConfig {
  retries: number;
  minTimeout: number;
  factor: number;
  onRetry?: (error: Error, attempt: number) => void;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  retries: 3,
  minTimeout: 1000,
  factor: 2,
  onRetry: (error, attempt) => {
    logger.warn(`Retrying function call (attempt ${attempt}):`, error.message);
  },
};

// Retry utility function
async function retryAsync<T>(
  fn: () => Promise<T>,
  config: RetryConfig = DEFAULT_RETRY_CONFIG,
): Promise<T> {
  let lastError: Error;

  for (let attempt = 0; attempt <= config.retries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      if (attempt === config.retries) {
        throw lastError;
      }

      // Don't retry authentication errors
      if (error instanceof AuthenticationError) {
        throw error;
      }

      // Don't retry validation errors
      if (error instanceof ValidationError) {
        throw error;
      }

      // Don't retry authorization errors
      if (error instanceof AuthorizationError) {
        throw error;
      }

      // Don't retry not found errors
      if (error instanceof NotFoundError) {
        throw error;
      }

      config.onRetry?.(lastError, attempt + 1);

      // Exponential backoff
      const timeout = config.minTimeout * Math.pow(config.factor, attempt);
      await new Promise((resolve) => setTimeout(resolve, timeout));
    }
  }

  throw lastError!;
}

// Main Firebase Functions client class
export class FirebaseFunctionsClient {
  private functions = functions;

  /**
   * Generic function caller with built-in auth, retry logic, and error handling
   */
  private async callFunction<TData = any, TResult = any>(
    functionName: string,
    data?: TData,
    retryConfig?: Partial<RetryConfig>,
  ): Promise<TResult> {
    // Wait for Firebase Auth to be ready if it's not immediately available
    if (!auth.currentUser) {
      // Wait a bit for Firebase Auth to initialize
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    logger.debug(`Calling function: ${functionName}`, {
      hasFirebaseUser: !!auth.currentUser,
      firebaseUserId: auth.currentUser?.uid,
    });

    return this.callWithFirebaseAuth<TData, TResult>(
      functionName,
      data,
      retryConfig,
    );
  }

  /**
   * Call Firebase Function using Firebase Auth
   */
  private async callWithFirebaseAuth<TData = any, TResult = any>(
    functionName: string,
    data?: TData,
    retryConfig?: Partial<RetryConfig>,
  ): Promise<TResult> {
    // Check token expiration before making the call
    if (auth.currentUser) {
      try {
        const tokenResult = await auth.currentUser.getIdTokenResult();
        if (tokenResult.expirationTime) {
          const expirationTime = new Date(tokenResult.expirationTime).getTime();
          const now = Date.now();

          // If token is expired or expiring within 1 minute, refresh it
          if (expirationTime <= now + 60000) {
            logger.info("Token expiring soon or expired, refreshing", {
              functionName,
              expirationTime: new Date(expirationTime).toISOString(),
              minutesRemaining: Math.floor(
                (expirationTime - now) / (60 * 1000),
              ),
            });
            await auth.currentUser.getIdToken(true); // Force refresh
          }
        }
      } catch (tokenError) {
        logger.warn("Failed to check/refresh token expiration", {
          functionName,
          error:
            tokenError instanceof Error ? tokenError.message : "Unknown error",
        });
        // Continue with call - Firebase SDK will handle token refresh if needed
      }
    }

    const callable = httpsCallable<TData, TResult>(
      this.functions,
      functionName,
    );

    return retryAsync(
      async () => {
        try {
          logger.debug(`Calling function with Firebase Auth: ${functionName}`, {
            data,
            userId: auth.currentUser?.uid,
          });
          const result = await callable(data);
          logger.debug(`Function ${functionName} completed successfully`);
          return result.data;
        } catch (error: any) {
          logger.error(`Function ${functionName} failed:`, error);
          const mappedError = this.mapError(error);

          // Handle authentication errors - attempt token refresh, then logout if fails
          if (
            mappedError instanceof AuthenticationError ||
            mappedError instanceof AuthorizationError
          ) {
            logger.warn("Authentication error in Firebase Function call", {
              functionName,
              error: mappedError.message,
            });

            // Try to refresh token
            if (auth.currentUser) {
              try {
                await auth.currentUser.getIdToken(true);
                logger.info("Token refreshed, retrying function call", {
                  functionName,
                });
                // Retry the call once with refreshed token
                try {
                  const retryResult = await callable(data);
                  return retryResult.data;
                } catch (retryError: any) {
                  logger.error("Retry after token refresh failed", {
                    functionName,
                    error:
                      retryError instanceof Error
                        ? retryError.message
                        : "Unknown error",
                  });
                  // If retry fails, trigger logout
                  await this.handleTokenExpiration();
                }
              } catch (refreshError) {
                logger.error("Token refresh failed, triggering logout", {
                  functionName,
                  error:
                    refreshError instanceof Error
                      ? refreshError.message
                      : "Unknown error",
                });
                await this.handleTokenExpiration();
              }
            } else {
              // No current user, trigger logout
              await this.handleTokenExpiration();
            }
          }

          throw mappedError;
        }
      },
      { ...DEFAULT_RETRY_CONFIG, ...retryConfig },
    );
  }

  /**
   * Handle token expiration by logging out user
   */
  private async handleTokenExpiration(): Promise<void> {
    try {
      // Import dynamically to avoid circular dependency
      const { useEnhancedAuthStore } = await import("../stores/enhancedAuth");
      await useEnhancedAuthStore.getState().logout();
    } catch (logoutError) {
      logger.error("Logout failed after token expiration", {
        error:
          logoutError instanceof Error ? logoutError.message : "Unknown error",
      });
    }
  }

  /**
   * Map Firebase Functions errors to application errors
   */
  private mapError(error: any): FirebaseFunctionsError {
    const code = error.code || "unknown";
    const message = error.message || "Unknown error";
    const details = error.details;

    switch (code) {
      case "functions/invalid-argument":
        return new ValidationError(message);
      case "functions/unauthenticated":
        return new AuthenticationError(message);
      case "functions/permission-denied":
        return new AuthorizationError(message);
      case "functions/not-found":
        return new NotFoundError(message);
      case "functions/resource-exhausted":
        return new RateLimitError(message);
      case "functions/already-exists":
        return new FirebaseFunctionsError(message, "already-exists", details);
      case "functions/failed-precondition":
        return new FirebaseFunctionsError(
          message,
          "failed-precondition",
          details,
        );
      case "functions/aborted":
        return new FirebaseFunctionsError(message, "aborted", details);
      case "functions/out-of-range":
        return new FirebaseFunctionsError(message, "out-of-range", details);
      case "functions/unimplemented":
        return new FirebaseFunctionsError(message, "unimplemented", details);
      case "functions/internal":
        return new FirebaseFunctionsError(message, "internal", details);
      case "functions/unavailable":
        return new FirebaseFunctionsError(message, "unavailable", details);
      case "functions/data-loss":
        return new FirebaseFunctionsError(message, "data-loss", details);
      default:
        return new FirebaseFunctionsError(
          `${message} (${code})`,
          code,
          details,
        );
    }
  }

  // User Management Functions
  async createUser(data: CreateUserRequest): Promise<CreateUserResponse> {
    return this.callFunction("createUser-createUser", data);
  }

  async getUser(data: GetUserRequest): Promise<GetUserResponse> {
    return this.callFunction("getUser-getUser", data);
  }

  async updateUser(data: UpdateUserRequest): Promise<UpdateUserResponse> {
    return this.callFunction("updateUser-updateUser", data);
  }

  async deleteUser(data: DeleteUserRequest): Promise<DeleteUserResponse> {
    return this.callFunction("deleteUser-deleteUser", data);
  }

  async listUsers(data: ListUsersRequest = {}): Promise<ListUsersResponse> {
    return this.callFunction("listUsers-listUsers", data);
  }

  // Client Management Functions
  async createClient(data: CreateClientRequest): Promise<CreateClientResponse> {
    return this.callFunction("createClient-createClient", data);
  }

  async updateClient(data: UpdateClientRequest): Promise<UpdateClientResponse> {
    return this.callFunction("updateClient-updateClient", data);
  }

  async listClients(
    data: ListClientsRequest = {},
  ): Promise<ListClientsResponse> {
    return this.callFunction("listClients-listClients", data);
  }

  // Session Management Functions
  async createSession(
    data: CreateSessionRequest,
  ): Promise<CreateSessionResponse> {
    return this.callFunction("createSession-createSession", data);
  }

  async getSession(data: GetSessionRequest): Promise<GetSessionResponse> {
    return this.callFunction("getSession-getSession", data);
  }

  async updateSession(
    data: UpdateSessionRequest,
  ): Promise<UpdateSessionResponse> {
    return this.callFunction("updateSession-updateSession", data);
  }

  async deleteSession(
    data: DeleteSessionRequest,
  ): Promise<DeleteSessionResponse> {
    return this.callFunction("deleteSession-deleteSession", data);
  }

  async listSessions(
    data: ListSessionsRequest = {},
  ): Promise<ListSessionsResponse> {
    return this.callFunction("listSessions-listSessions", data);
  }

  // Usage Tracking Functions
  async trackGeneration(
    data: TrackGenerationRequest,
  ): Promise<TrackGenerationResponse> {
    return this.callFunction("trackGeneration-trackGeneration", data);
  }
}

// Usage Tracking Types
export type GenerationType = 'generate' | 'edit';

export interface TrackGenerationRequest {
  type: GenerationType;
  clientId?: string;
  sessionId?: string;
  metadata?: {
    modality?: string;
    style?: string;
    promptLength?: number;
  };
}

export interface TrackGenerationResponse {
  success: boolean;
  data: {
    generationId: string;
    usage: {
      month: string;
      generationsCreated: number;
      editsCreated: number;
      totalOperations: number;
    };
  };
}

// Export singleton instance
export const firebaseFunctions = new FirebaseFunctionsClient();

// Types are already exported via interface declarations above
